/**
 * DIRECTIONAL_ICONS — the curated RTL-flip allow-list.
 *
 * DS-04 / UI-SPEC §RTL & Accessibility: only icons in this Set flip when
 * `<html dir="rtl">`. Non-directional glyphs (logos, status indicators,
 * calendar, search, settings, bell, download, eye, etc.) NEVER flip — flipping
 * them destroys their meaning in Arabic contexts.
 *
 * The names are Lucide kebab-case identifiers (what `<Icon>`'s `name` prop
 * accepts). Keep this list the single source of truth — ADR-style changes only.
 *
 * Reference: Lucide icon catalog (https://lucide.dev/icons/) filtered to
 * inherently direction-bearing glyphs (chevrons, arrows, undo/redo, send/reply,
 * navigational-flow, list-indent). 38 entries — satisfies acceptance criterion
 * "≥30 directional icons".
 */
export const DIRECTIONAL_ICONS: ReadonlySet<string> = new Set<string>([
  // Chevrons
  "chevron-left",
  "chevron-right",
  "chevrons-left",
  "chevrons-right",

  // Arrows (linear)
  "arrow-left",
  "arrow-right",
  "arrow-left-circle",
  "arrow-right-circle",
  "arrow-big-left",
  "arrow-big-right",
  "arrow-left-from-line",
  "arrow-right-from-line",
  "arrow-left-to-line",
  "arrow-right-to-line",

  // Corner arrows — direction-bearing
  "corner-down-left",
  "corner-down-right",
  "corner-up-left",
  "corner-up-right",
  "corner-left-down",
  "corner-right-down",
  "corner-left-up",
  "corner-right-up",

  // Movement / flow
  "move-left",
  "move-right",
  "skip-back",
  "skip-forward",
  "fast-forward",
  "rewind",

  // Actions that encode flow direction
  "undo",
  "redo",
  "undo-2",
  "redo-2",
  "reply",
  "reply-all",
  "send",
  "send-horizontal",

  // List indent direction
  "indent",
  "outdent",
  "list-ordered",

  // Panel open/close (edge-bound)
  "panel-left-open",
  "panel-left-close",
  "panel-right-open",
  "panel-right-close",

  // Auth flow (door-in / door-out is directional)
  "log-in",
  "log-out",
]);

/**
 * Runtime guard — consumers typically do not need this (the Icon component
 * handles lookup internally), but it's exported for test fixtures + docs.
 */
export function isDirectionalIconName(name: string): boolean {
  return DIRECTIONAL_ICONS.has(name);
}
